import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import {Button, message, Select, Tooltip} from 'antd';
import OrderListingQueryHint from '../../../../components/filters/OrderListingQueryHint';
import {DownOutlined} from '@ant-design/icons';
import {gql, useMutation, useQuery} from '@apollo/client';
import {getUserInfo} from "../../../../utils/auth";

import {OrderWithAddressType} from "../hooks/useCentralExceptionQuery";
import {MARK_AS_EXCEPTION} from '../../../../graphql/mutations/markAsException';
import {allChannelResponse, GET_ALL_CHANNEL} from "../graphql/queries/getChannelAll";
import MarkAsExceptionModal, {MarkAsExceptionData} from "../../../../components/MarkAsExceptionModal";
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import BatchInputFilterItem from "../../../delivery/components/BatchInputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import DateRangeFilterItem from "../../../delivery/components/DateRangeFilterItem";
import { overallStatusOptions } from "../../../../config/filterOptions";
import { GET_INT_ENUM_VALUES, GetIntEnumValuesResponse } from '../../../../graphql/queries/getEnumValues';
import {OrderFiltersType} from "../../../../graphql/types/OrderFiltersType";
import {ExceptionDictionaryResponse,ExceptionDictionaryType} from "../graphql/queries/getAllException"
import {
    GET_ALL_EXCEPTION_DICT_FOR_CS,
    GET_NEXT_ACTION_LIST_FOR_CS,
    GET_WAREHOUSES_FOR_CS
} from "../../../qualitycontrol/graphql/queries/customerServiceSharedQueries"
import {filterExceptionByUserRole} from "../../../../config/roleExceptionConfig";
import { NextActionListResponse, NextActionListType } from '../../../../graphql/queries/getNextActionList';
import {SiteType} from "graphql/types/SiteType";
import {WarehousesResponse} from '../graphql/queries/getWarehouseForCentral';
import { useTranslation } from 'react-i18next';
import { useStatusInternationalization } from 'pages/delivery/parcels/hooks/useStatusInternationalization';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { getLastWeekDateRange } from '../../../../utils/dateTimeUtils';
import { useAutoClearDefaultTime } from '../../../../hooks/useAutoClearDefaultTime';
interface CentralExceptionFilterProps {
    filters: OrderFiltersType;
    setFilters: (filters: OrderFiltersType) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    selectedRows: OrderWithAddressType[];
}

interface CustomerOption {
    value: string;
    label: string;
}

const CentralExceptionFilter: React.FC<CentralExceptionFilterProps> = ({
    filters,
    setFilters,
    handleFilter,
    handleResetFilters,
    selectedRows,
}) => {
    const [markAsExceptionVisible, setMarkAsExceptionVisible] = useState(false);
    const [markAsExceptionLoading, setMarkAsExceptionLoading] = useState(false);
    const [markAsException] = useMutation(MARK_AS_EXCEPTION);
    const userRole = getUserInfo()?.account?.role?.name;
    const [channelOptions, setChannelOptions] = useState<CustomerOption[]>([]);
    const {data} = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const {t} = useTranslation();
    const [exceptionSelectData, setExceptionSelectData] = useState<ExceptionDictionaryType[]>([]);
    const {data:exceptionData} = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION_DICT_FOR_CS);
    const {data:nextActionData} = useQuery<NextActionListResponse>(GET_NEXT_ACTION_LIST_FOR_CS);
    const exceptionList = exceptionData?.getExceptionDictionaryAll || [];
    const nextActionSelectData: NextActionListType[] = nextActionData?.getNextActionList || [];
    const { data: warehouseData } = useQuery<WarehousesResponse>(GET_WAREHOUSES_FOR_CS);
    const { data: exceptionStatusEnumData } = useQuery<GetIntEnumValuesResponse>(GET_INT_ENUM_VALUES, {
        variables: { enumType: 'ExceptionOrderStatus' }
    });
    const { currentWarehouse } = useWarehouseContext();
    const siteType = currentWarehouse?.siteType;
    // 仓库列表状态管理
    const [warehouseListForHubData, setWarehouseListForHubData] = useState<any[]>([]);
    const { getExceptionReasonText, getNextActionTextFromBackend } = useStatusInternationalization();

    // 异常状态选项（从后端枚举获取）
    const exceptionOrderStatusOptions = React.useMemo(() => {
        const enumValues = exceptionStatusEnumData?.getIntEnumValues || [];
        return enumValues.map(item => ({
            value: String(item.value),
            label: t(`central.exceptions.exceptionStatus.${item.name}`)
        }));
    }, [exceptionStatusEnumData, t]);
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();
    // - undefined：未初始化，默认最近一周
    // - null：用户手动清空（点击叉号），保持为空
    // 一旦用户动过时间（包含点叉号清空），在 reset/刷新前都不再自动清空默认时间
    const hasUserEditedMarkExceptionAtRef = useRef(false);
    useAutoClearDefaultTime({
        filters,
        setFilters,
        timeKey: 'markExceptionAt',
        hasUserEditedTimeRef: hasUserEditedMarkExceptionAtRef,
    });

    const getEffectiveMarkExceptionAt = (value: any): [string | null, string | null] | null => {
        if (value === null) return null;
        const isValid = value &&
            Array.isArray(value) &&
            value.length === 2;
        return isValid ? (value as [string | null, string | null]) : getLastWeekDateRange();
    };
    const col1Labels = [
        t('central.exceptions.filters.tracking'),
        t('central.exceptions.filters.client'),
        t('central.exceptions.filters.exceptionTime'),
        t('central.exceptions.filters.exceptionPlaceHub'),
    ];
    const col2Labels = [
        t('central.exceptions.filters.parcelStatus'),
        t('central.exceptions.filters.exceptionStatus'),
        t('central.exceptions.filters.exceptionPlaceWhs'),
    ];
    const col3Labels = [
        t('central.exceptions.filters.exceptionReason'),
        t('central.exceptions.filters.nextAction'),
    ];
    const col1SpanClassName = getSmartLabelClassName(col1Labels);
    const col2SpanClassName = getSmartLabelClassName(col2Labels);
    const col3SpanClassName = getSmartLabelClassName(col3Labels);
    useEffect(() => {
        if (data?.getAllChannels) {
            const channels = Array.isArray(data.getAllChannels) ? data.getAllChannels : [data.getAllChannels];
            const options = channels.map(channel => ({
                value: channel.id?.toString() || '',  // 确保value是字符串类型
                label: channel.channelName
            }));
            setChannelOptions(options);
        }
        if (exceptionList){
            const exceptionData = filterExceptionByUserRole(exceptionList, userRole || '', siteType);
            setExceptionSelectData(exceptionData);
        }
    }, [data, exceptionData, exceptionList, userRole, siteType]);

    // 监听 Hub 选择变化，更新仓库列表
    useEffect(() => {
        if (filters.exceptionHubId && warehouseData?.warehouses) {
            const filteredWarehouses = warehouseData.warehouses
                .filter(warehouse => warehouse.siteType === SiteType.WAREHOUSE)
                .filter(warehouse => String(warehouse.parentSiteId) === String(filters.exceptionHubId));
            setWarehouseListForHubData(filteredWarehouses);
        } else {
            setWarehouseListForHubData([]);
        }
    }, [filters.exceptionHubId, warehouseData?.warehouses]);
    
    // 处理时间变化的函数
    const handleExceptionTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        hasUserEditedMarkExceptionAtRef.current = true;
        setFilters({...filters, markExceptionAt: dates});
    };

    // Hub 切换处理函数
    const handleHubChange = (value: string) => {
        if (value && warehouseData?.warehouses) {
            const filteredWarehouses = warehouseData.warehouses
                .filter(warehouse => warehouse.siteType === SiteType.WAREHOUSE)
                .filter(warehouse => String(warehouse.parentSiteId) === String(value));
            setWarehouseListForHubData(filteredWarehouses);
        } else {
            setWarehouseListForHubData([]);
        }
        
        setFilters({
            ...filters, 
            exceptionHubId: value || undefined,
            exceptionWarehouseId: undefined
        });
    };

    const handleMarkAsExceptionConfirm = async (formData: MarkAsExceptionData) => {
        try {
            setMarkAsExceptionLoading(true);
            await markAsException({
                variables: {
                    orderId: selectedRows[0].id,
                    exceptionReasonCode: formData.exceptionReasonCode,
                    exceptionHubId: formData.exceptionHubId,
                    exceptionWarehouseId: formData.exceptionWarehouseId,
                    exceptionImageS3KeysJson: formData.photos
                }
            })

            message.success('Marking exception successfully');
            setMarkAsExceptionVisible(false);
            handleFilter();
        } catch (error) {
            console.error('Marking exception failed:', error);
            message.error('Marking exception failed');
        } finally {
            setMarkAsExceptionLoading(false);
        }
    };

    return (
        <div className="mb-2 pl-0 py-2">
            <OrderListingQueryHint />
            {/*  异常相关的筛选条件 - 优化三等分布局 */}
            <div>
                {/* Tracking 独占一行 */}
                <div className="mb-4">
                    <BatchInputFilterItem
                        label={t('central.exceptions.filters.tracking')}
                        value={filters.trackingNumbers}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({
                            ...filters,
                            trackingNumbers: e.target.value || undefined
                        })}
                        placeholder={t('central.parcels.placeholders.trackingNumbers') || ''}
                        spanClassName={col1SpanClassName}
                        rows={2}
                    />
                </div>

                {/* 使用 Grid 布局确保列对齐 */}
                <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('central.exceptions.filters.client')}
                            options={channelOptions}
                            value={(() => {
                                // 解决:选中Option后,显示label，Chrome F5刷新后，显示ID的问题
                                const currentValue = filters.channelId;
                                if (!currentValue) {
                                    return undefined;
                                }
                                if (!data?.getAllChannels) {
                                    return undefined;
                                }
                                return currentValue?.toString() || '';
                            })()}
                            onChange={(value: string) => setFilters({...filters, channelId: value ? [value] : undefined})}
                            className="w-full"
                            spanClassName={col1SpanClassName}
                        />
                    </div>

                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('central.exceptions.filters.parcelStatus')}
                            options={overallStatusOptions}
                            mode="multiple"
                            maxTagCount="responsive"
                            value={filters.overallStatus ?? []}
                            onChange={(values: string[]) => setFilters({
                                ...filters,
                                overallStatus: values && values.length > 0 ? values : undefined
                            })}
                            className="w-full"
                            spanClassName={col2SpanClassName}
                        />
                    </div>

                    <div className="min-w-0">
                        <div className={containerClassName}>
                            <span className={col3SpanClassName}>{t('central.exceptions.filters.exceptionReason')}</span>
                            <div className={`${inputContainerClassName} flex items-center`}>
                                <Select
                                    mode="multiple"
                                    value={filters.exceptionReasonCodes ?? []}
                                    onChange={(values: string[]) => setFilters({...filters, exceptionReasonCodes: values.length > 0 ? values : undefined})}
                                    className="w-full"
                                    placeholder={t('components.input.all')}
                                    allowClear
                                    suffixIcon={<DownOutlined />}
                                    maxTagCount="responsive"
                                    size="middle"
                                    optionRender={(option) => {
                                        const label = option.label as string;
                                        if (label.length > 30) {
                                            return (
                                                <Tooltip title={label} placement="topLeft">
                                                    <span>{label.substring(0, 30)}...</span>
                                                </Tooltip>
                                            );
                                        }
                                        return label;
                                    }}
                                >
                                    {exceptionSelectData.map(ex => {
                                        // 🌍 使用国际化函数替换硬编码逻辑
                                        const label = getExceptionReasonText(ex);
                                        return (
                                            <Select.Option key={ex.exceptionReasonCode} value={ex.exceptionReasonCode}>
                                                {label}
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            </div>
                        </div>
                    </div>
                    {/* 第二行 */}
                    <div className="min-w-0">
                        <DateRangeFilterItem
                            label={t('central.exceptions.filters.exceptionTime')}
                            value={getEffectiveMarkExceptionAt(filters.markExceptionAt)}
                            onChange={handleExceptionTimeChange}
                            className="w-full"
                            spanClassName={col1SpanClassName}
                        />
                    </div>

                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('central.exceptions.filters.exceptionStatus')}
                            options={exceptionOrderStatusOptions}
                            mode="multiple"
                            maxTagCount="responsive"
                            value={filters.exceptionStatuses?.map(String) ?? []}
                            onChange={(values: string[]) => setFilters({
                                ...filters,
                                exceptionStatuses: values && values.length > 0 && values.length < exceptionOrderStatusOptions.length ? values.map(Number) : undefined
                            })}
                            className="w-full"
                            spanClassName={col2SpanClassName}
                        />
                    </div>

                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('central.exceptions.filters.nextAction')}
                            options={nextActionSelectData.filter(item => item.value !== 0).map(option => ({
                                value: option.name,
                                label: getNextActionTextFromBackend(option.name)
                            }))}
                            value={filters.nextAction ?? undefined}
                            onChange={(value: string) => setFilters({...filters, nextAction: value || undefined})}
                            className="w-full"
                            spanClassName={col3SpanClassName}
                            placeholder={t('components.input.select') as string}
                        />
                    </div>
                    {/* 第三行 */}
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('central.exceptions.filters.exceptionPlaceHub')}
                            options={[
                                ...(warehouseData?.warehouses.filter(option => option.siteType === SiteType.HUB).map(option => ({
                                    value: option.id?.toString() || '',
                                    label: option.name || ''
                                })) || [])
                            ]}
                            value={(() => {
                                // 解决:选中Option后,显示label，Chrome F5刷新后，显示ID的问题
                                const currentValue = filters.exceptionHubId;
                                if (!currentValue) {
                                    return undefined;
                                }
                                if (!warehouseData?.warehouses) {
                                    return undefined;
                                }
                                return currentValue?.toString() || '';
                            })()}
                            onChange={handleHubChange}
                            className="w-full"
                            spanClassName={col1SpanClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('central.exceptions.filters.exceptionPlaceWhs')}
                            options={[
                                { value: '0', label: 'N/A' },
                                // 级联逻辑：根据Hub选择状态决定显示哪些仓库
                                ...((() => {
                                    if (filters.exceptionHubId) {
                                        if (warehouseListForHubData.length > 0) {
                                            return warehouseListForHubData.map(option => ({
                                                value: option.id?.toString() || '',
                                                label: option.name || ''
                                            }));
                                        } else if (warehouseData?.warehouses) { 
                                            const hubWarehouses = warehouseData.warehouses
                                                .filter(warehouse => warehouse.siteType === SiteType.WAREHOUSE)
                                                .filter(warehouse => String(warehouse.parentSiteId) === String(filters.exceptionHubId));
                                            return hubWarehouses.map(option => ({
                                                value: option.id?.toString() || '',
                                                label: option.name || ''
                                            }));
                                        }
                                        return [];
                                    } else {
                                        return warehouseData?.warehouses.filter(option => option.siteType === SiteType.WAREHOUSE).map(option => ({
                                            value: option.id?.toString() || '',
                                            label: option.name || ''
                                        })) || [];
                                    }
                                })())
                            ]}
                            value={(() => {
                                // 解决:选中Option后,显示label，Chrome F5刷新后，显示ID的问题(包括N/A选项和级联关系处理)
                                const currentValue = filters.exceptionWarehouseId;
                                if (currentValue === '0' || Number(currentValue) === 0) {
                                    return '0';
                                }
                                if (!currentValue) {
                                    return undefined;
                                }
                                if (!warehouseData?.warehouses) {
                                    return undefined;
                                }
                                if (filters.exceptionHubId) {
                                    const hubWarehouses = warehouseData.warehouses
                                        .filter(warehouse => warehouse.siteType === SiteType.WAREHOUSE)
                                        .filter(warehouse => String(warehouse.parentSiteId) === String(filters.exceptionHubId));
                                    
                                    const isValidForHub = hubWarehouses.some(w => w.id?.toString() === currentValue?.toString());
                                    if (isValidForHub) {
                                        return currentValue?.toString() || '';
                                    }
                                    return undefined;
                                }
                                return currentValue?.toString() || '';
                            })()}
                            onChange={(value: string) => setFilters({...filters, exceptionWarehouseId: value || undefined})}
                            className="w-full"
                            spanClassName={col2SpanClassName}
                        />
                    </div>
                    <div className="min-w-0">
                    </div>
                </div>
            </div>


            {/* 异常标记弹窗（单个：可能已经废弃） */}
            {markAsExceptionVisible && selectedRows[0] && (
            <MarkAsExceptionModal
                visible={markAsExceptionVisible}
                onClose={() => setMarkAsExceptionVisible(false)}
                onConfirm={handleMarkAsExceptionConfirm}
                loading={markAsExceptionLoading}
                parcel={selectedRows[0] as any}
                userRole={userRole || ''}
            />
            )}
        </div>
    );
};

export default CentralExceptionFilter; 